/**
 * eSign — Agreements dashboard. Rebuilt to match Collings-eSign's AgreementsPage:
 * two-pane shell (bucket sidebar + table), rich status labels ("Need to sign",
 * "Waiting for X"), conditional row actions + kebab menu, search, and pagination.
 */
import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams, useLocation, Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import {
  FileSignature, Plus, Send, Ban, Download, Trash2, X, Search, MoreVertical,
  Check, Pencil, FileText, ChevronLeft, ChevronRight, Sparkles, ScrollText, Home, Upload, Eye,
  Lock, Gavel,
} from 'lucide-react';
import esignApi from '../../../api/esign';
import { unsignedEditableRecipients } from '../utils/recipientFix';
import { getContacts } from '../../../api/contact';
import { searchProperties } from '../../../api/index';
import { getPropertyDocuments, getProperty } from '../../../api/agent';
import { canViewAgencyWide } from "../../../utils/roleAccess";
import { formatMelbourneDayLabel } from "../../../utils/melbourneTime";
import { cleanPersonName, personDisplayName } from "../../../utils/personName";

const PER_PAGE = 10;

// A distinct leading icon per templated authority so the tiles aren't three
// identical scrolls (CG, 2 Oct 2026: "change the icon on each so it's not
// confusing"). Falls back to the generic scroll for any future/unknown key.
const AUTHORITY_ICONS = {
  general_sales: FileText,     // plain document
  exclusive_sale: Lock,        // exclusive / private sale
  exclusive_auction: Gavel,    // auction gavel
};

// Staff/admin accounts that own some eSign documents but aren't sales agents, so
// they must not appear as pickable agents in the agency-wide filter (CG, 2 Oct
// 2026: these are not official agents). Matched on the owner's display name.
const NON_AGENT_OWNERS = new Set([
  'Wally Gaynor',
  'Joshua Alejandro C. Diokno',
]);

const BUCKETS = [
  { key: 'all', label: 'All agreements' },
  { key: 'drafts', label: 'Drafts' },
  { key: 'in_progress', label: 'In Progress' },
  { key: 'at_risk', label: 'At Risk' },
  { key: 'completed', label: 'Completed' },
  { key: 'void', label: 'Void' },
  { key: 'deleted', label: 'Deleted' },
];
const BUCKET_TITLE = {
  all: 'All agreements', drafts: 'Drafts', in_progress: 'In Progress',
  at_risk: 'At Risk', completed: 'Completed', void: 'Void', deleted: 'Deleted',
};

// doc.status -> pill colour (matches eSign: colour is driven by the raw status).
const PILL = {
  draft: 'bg-gray-100 text-gray-600',
  pending: 'bg-amber-100 text-amber-800',
  completed: 'bg-green-100 text-green-800',
  voided: 'bg-purple-100 text-purple-700',
  deleted: 'bg-gray-100 text-gray-400',
};

const bucketOf = (status) =>
  ({ draft: 'drafts', pending: 'in_progress', completed: 'completed', voided: 'void', deleted: 'deleted' }[status] || 'all');

// "At risk": a pending agreement outstanding too long without being fully signed,
// so it can be chased before it goes stale (DOMA 13201157349 S3). Measured from
// the earliest signing email (else when it was sent/created).
const AT_RISK_DAYS = 7;
function outstandingSince(doc) {
  const sent = (doc.sign_requests || []).map((sr) => sr.email_sent_at).filter(Boolean).sort();
  return sent[0] || doc.sent_at || doc.created_at || null;
}
function isAtRisk(doc) {
  if (doc.status !== 'pending') return false;
  const srs = doc.sign_requests || [];
  if (!srs.length || srs.every((sr) => sr.status === 'signed')) return false;
  const since = outstandingSince(doc);
  if (!since) return false;
  return (Date.now() - new Date(since).getTime()) / 86400000 >= AT_RISK_DAYS;
}

function statusLabel(doc) {
  if (doc.status === 'draft') return 'Draft';
  if (doc.status === 'completed') return 'Completed';
  if (doc.status === 'voided') return 'Voided';
  if (doc.status === 'deleted') return 'Deleted';
  const srs = doc.sign_requests || [];
  // RTA "Generated": a templated authority that's been generated for the per-party
  // launcher but not yet delivered to / actioned by any signer. The agent signing on
  // the form does NOT count as delivery, so a hybrid agent-signed doc still reads
  // "Generated" until a party is emailed offsite or opens their link.
  if (doc.authority_key) {
    const delivered = srs.some((sr) => sr.email_sent_at || sr.status === 'viewed' || sr.status === 'declined');
    const anyPending = srs.some((sr) => sr.status === 'pending');
    if (anyPending && !delivered) return 'Generated';
  }
  if (doc.my_sign_request && doc.my_sign_request.status !== 'signed') return 'Need to sign';
  const unsigned = srs.filter((sr) => sr.status !== 'signed');
  if (unsigned.length === 0) return 'Completed';
  if (unsigned.length === 1) return `Waiting for ${unsigned[0].signer_name || unsigned[0].signer_email || 'signer'}`;
  return `Waiting for ${unsigned.length} others`;
}

// When a recipient first opened (viewed) this document, or null. CG
// (#12986... eSign): "put here if the contract has been opened and the time and
// date. It only shows opened on the dashboard." Uses the earliest first_viewed_at
// across recipients, i.e. when the document was first opened by anyone.
function firstOpenedAt(doc) {
  const times = (doc.sign_requests || []).map((sr) => sr.first_viewed_at).filter(Boolean).sort();
  return times[0] || null;
}

// Pill colour: driven by raw status, except the derived "Generated" state gets its
// own (blue) pill to match RTA's list.
function pillClass(doc) {
  if (statusLabel(doc) === 'Generated') return 'bg-blue-100 text-blue-800';
  return PILL[doc.status] || 'bg-gray-100 text-gray-600';
}

function recipientsLine(doc, meEmail = '') {
  const srs = doc.sign_requests || [];
  if (!srs.length) return 'Recipients not set';
  return 'To: ' + srs.map((sr) => {
    if (meEmail && (sr.signer_email || '').toLowerCase() === meEmail) return 'me';
    return sr.signer_name || sr.signer_email || '—';
  }).join(', ');
}

const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : '—');

// Keep only the first recipient for each email (case-insensitive); drop blanks.
function dedupeRecipients(list) {
  const seen = new Set();
  return (list || []).filter((r) => {
    const e = (r.email || '').trim().toLowerCase();
    if (!e || seen.has(e)) return false;
    seen.add(e);
    return true;
  });
}

const VALID_BUCKETS = ['all', 'drafts', 'in_progress', 'at_risk', 'completed', 'void', 'deleted'];

export default function EsignAgreementsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const me = useSelector((s) => s.auth?.user);
  const meEmail = (me?.email || '').toLowerCase();
  // The agency principal only: see every agent's agreements instead of just
  // their own (CG: "show all agents agreements data, I would like to follow how
  // many are in progress"). Defaults off so nobody's own view is affected.
  //
  // This used to be hasAdminAccess, which honours is_staff / is_superuser - so
  // every admin, and the one sales agent whose account carries those flags, was
  // offered the whole agency (Christian Gravias, 7 Sep 2026, Monday
  // 12984138077). canViewAgencyWide mirrors the backend gate exactly.
  const canSeeEveryAgent = canViewAgencyWide(me);
  const [agencyWide, setAgencyWide] = useState(false);
  const [agentFilter, setAgentFilter] = useState('all');
  const [sortOrder, setSortOrder] = useState('desc'); // 'desc' = newest to oldest
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  const [documents, setDocuments] = useState([]);
  const [deleted, setDeleted] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bucket, setBucket] = useState(() => {
    const b = searchParams.get('bucket');
    return VALID_BUCKETS.includes(b) ? b : 'all';
  });
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  // New-document flow: null | 'choose' | 'contract' | 'authority:<key>'
  const [newFlow, setNewFlow] = useState(null);
  const [menuOpenId, setMenuOpenId] = useState(null);
  const [modal, setModal] = useState(null); // {type:'void'|'template', doc}
  const [editDoc, setEditDoc] = useState(null); // draft whose details/recipients are being edited
  const [fixEmailDoc, setFixEmailDoc] = useState(null); // sent doc whose unsigned recipient email is being corrected
  const [busyId, setBusyId] = useState(null);
  // Prefill for the offer flow's "Fill Out Contract" deep link
  // (?newFlow=contract&propertyId=&buyerName=&buyerEmail=), set once resolved.
  const [contractPrefill, setContractPrefill] = useState(null);

  useEffect(() => {
    const flow = searchParams.get('newFlow');
    const propertyId = searchParams.get('propertyId');
    if (flow !== 'contract' || !propertyId) return;
    const buyerName = searchParams.get('buyerName') || '';
    const buyerEmail = searchParams.get('buyerEmail') || '';
    // The buyer's offer itself, handed over in navigation state by the Offers
    // tab / Buyer Portal Offers pop-up, so its figures can prefill the contract
    // (CG, 16 Aug 2026). Absent when the flow is opened any other way.
    const offer = location.state?.offer || null;
    // Clear the params immediately so this doesn't re-trigger on remount.
    setSearchParams({});
    getProperty(propertyId)
      .then(({ data }) => {
        setContractPrefill({
          property: data,
          offer,
          recipient: { name: buyerName, email: buyerEmail, role: 'signer', order: 1 },
        });
        setNewFlow('contract');
      })
      .catch(() => toast.error('Could not load that property.'));
    // Only run once on mount — deliberately not re-run when searchParams
    // changes since we clear them above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await esignApi.agreements(
        agencyWide ? { agency_wide: 'true' } : {}
      );
      // Accept the new {documents, deleted} shape AND the legacy {owned, to_sign} shape
      // (so the list works even if the backend server hasn't reloaded the new code yet).
      let docs = data.documents;
      if (!docs) {
        const merged = [...(data.owned || []), ...(data.to_sign || [])];
        const seen = new Set();
        docs = merged.filter((d) => (seen.has(d.id) ? false : (seen.add(d.id), true)));
      }
      setDocuments(docs || []);
      setDeleted(data.deleted || []);
    } catch {
      toast.error('Failed to load agreements.');
    } finally {
      setLoading(false);
    }
  }, [agencyWide]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [bucket, query, agentFilter, dateFrom, dateTo, sortOrder]);
  useEffect(() => { if (!agencyWide) setAgentFilter('all'); }, [agencyWide]);

  // Deep links from the home dashboard: ?new=1 opens the New-agreement chooser,
  // ?bucket=… preselects a bucket. Consume the params so a refresh doesn't re-trigger.
  useEffect(() => {
    if (searchParams.get('new') === '1') {
      setNewFlow('choose');
      const next = new URLSearchParams(searchParams);
      next.delete('new');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // In the agency-wide view, drop documents owned by non-agent staff accounts so
  // their agreements never appear in the list, counts, or filter (CG, 2 Oct 2026:
  // these are not official agents). Single source of truth for everything below.
  const visibleDocs = useMemo(
    () => (agencyWide ? documents.filter((d) => !NON_AGENT_OWNERS.has(d.owner_name)) : documents),
    [agencyWide, documents]);
  const visibleDeleted = useMemo(
    () => (agencyWide ? deleted.filter((d) => !NON_AGENT_OWNERS.has(d.owner_name)) : deleted),
    [agencyWide, deleted]);

  const counts = useMemo(() => {
    const c = { all: visibleDocs.length, drafts: 0, in_progress: 0, at_risk: 0, completed: 0, void: 0, deleted: visibleDeleted.length };
    visibleDocs.forEach((d) => { const b = bucketOf(d.status); if (c[b] != null) c[b] += 1; });
    c.at_risk = visibleDocs.filter(isAtRisk).length;
    return c;
  }, [visibleDocs, visibleDeleted]);

  // Only meaningful in the agency-wide view - every document is the current
  // user's own otherwise, so an agent filter would just be a no-op dropdown.
  const agentOptions = useMemo(() => {
    if (!agencyWide) return [];
    const names = new Set(visibleDocs.concat(visibleDeleted).map((d) => d.owner_name).filter(Boolean));
    return Array.from(names).sort();
  }, [agencyWide, visibleDocs, visibleDeleted]);

  const filtered = useMemo(() => {
    const source = bucket === 'deleted' ? visibleDeleted : visibleDocs;
    const q = query.trim().toLowerCase();
    // Inclusive on both ends - "to" covers the whole day, not just midnight.
    const fromTime = dateFrom ? new Date(`${dateFrom}T00:00:00`).getTime() : null;
    const toTime = dateTo ? new Date(`${dateTo}T23:59:59.999`).getTime() : null;
    const rows = source.filter((d) => {
      if (bucket === 'at_risk') { if (!isAtRisk(d)) return false; }
      else if (bucket !== 'all' && bucket !== 'deleted' && bucketOf(d.status) !== bucket) return false;
      if (agencyWide && agentFilter !== 'all' && d.owner_name !== agentFilter) return false;
      if (fromTime != null || toTime != null) {
        const t = new Date(d.updated_at || d.created_at).getTime();
        if (Number.isNaN(t)) return false;
        if (fromTime != null && t < fromTime) return false;
        if (toTime != null && t > toTime) return false;
      }
      if (q) {
        const recipients = (d.sign_requests || []).map((sr) => `${sr.signer_name || ''} ${sr.signer_email || ''}`).join(' ');
        const haystack = `${d.title || ''} ${d.owner_name || ''} ${recipients}`.toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
    const sorted = [...rows].sort((a, b) => {
      const ta = new Date(a.updated_at || a.created_at).getTime();
      const tb = new Date(b.updated_at || b.created_at).getTime();
      return sortOrder === 'desc' ? tb - ta : ta - tb;
    });
    return sorted;
  }, [bucket, visibleDeleted, visibleDocs, query, agencyWide, agentFilter, dateFrom, dateTo, sortOrder]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const paginated = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  // ---- actions ----
  const doDownload = async (doc) => {
    try {
      // Always serve the freshly-baked, pre-filled PDF. The field values are only
      // an on-screen overlay, so the raw file on disk is blank (Simon/CG). The
      // baked render also matches the signed copy once completed, and heals the
      // brief window where a just-completed doc's signed file is still baking.
      const res = await esignApi.previewBlob(doc.id);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${doc.title || 'document'}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error('Could not download.'); }
  };
  const doResend = async (doc) => {
    setBusyId(doc.id);
    try {
      const { data } = await esignApi.resend(doc.id);
      toast.success(`Resent to ${data.resent ?? 0} recipient(s).`);
    } catch (e) { toast.error(e?.response?.data?.error || 'Resend failed.'); }
    finally { setBusyId(null); }
  };
  const doDelete = async (doc) => {
    if (!window.confirm(`Move "${doc.title}" to trash?`)) return;
    setBusyId(doc.id); setMenuOpenId(null);
    try { await esignApi.deleteEnvelope(doc.id); toast.success('Moved to trash.'); load(); }
    catch { toast.error('Delete failed.'); } finally { setBusyId(null); }
  };
  const confirmVoid = async () => {
    const doc = modal.doc; setBusyId(doc.id);
    try { await esignApi.void(doc.id); toast.success('Document voided.'); setModal(null); load(); }
    catch (e) { toast.error(e?.response?.data?.error || 'Void failed.'); }
    finally { setBusyId(null); }
  };
  const confirmTemplate = async () => {
    const doc = modal.doc; setBusyId(doc.id);
    try { await esignApi.saveAsTemplate(doc.id); toast.success('Saved as template.'); setModal(null); }
    catch { toast.error('Could not save as template.'); } finally { setBusyId(null); }
  };
  // Amend a completed authority IN PLACE (DOMA 13177543076): re-open it on the
  // SAME document (keeping every signature), then open the form so the agent can
  // edit, re-initial and send the change for the other parties to re-initial.
  const doAmend = async (doc) => {
    setMenuOpenId(null);
    setBusyId(doc.id);
    try {
      const { data } = await esignApi.amend(doc.id);
      toast.success('Amending — edit the change, then re-initial and send.');
      navigate(editHref(data));
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Could not open this authority to amend.');
    } finally { setBusyId(null); }
  };

  // Templated authorities are prepared via the tabbed form (RTA-style); everything
  // else uses the raw PDF field editor.
  const editHref = (doc) =>
    doc.doc_kind === 'contract_of_sale'
      ? `/esign/contract-of-sale/${doc.id}`
      : doc.authority_key
      ? `/esign/authority/${doc.id}`
      : `/esign/envelope/${doc.id}`;

  const openTitle = (doc) => {
    // A draft I own is still being prepared — open the EDITOR (form for authorities,
    // field editor otherwise), never the signing page, even though I'm listed as a
    // signer (authorities/templates add the agent up front). Signing starts on send.
    if (doc.is_owner && doc.status === 'draft') {
      navigate(editHref(doc));
      return;
    }
    // Signer who still needs to sign -> signing page. Only for SENT docs (pending):
    // an unsent draft is never signable, no matter who's listed as a signer.
    if (doc.status === 'pending' && doc.my_sign_request && doc.my_sign_request.status !== 'signed') {
      navigate(`/esign/sign/${doc.my_sign_request.token}`);
      return;
    }
    if (!doc.is_owner) { navigate(`/esign/document/${doc.id}`); return; }
    // Owner: the editor is ONLY for editable docs (draft, or pending with no signature yet).
    const anySigned = (doc.sign_requests || []).some((sr) => sr.status === 'signed');
    const editable = doc.status === 'draft' || (doc.status === 'pending' && !anySigned);
    navigate(editable ? editHref(doc) : `/esign/document/${doc.id}`);
  };

  return (
    <div className="flex flex-col md:flex-row md:h-[calc(100vh-4rem)]">
      {/* Sidebar */}
      <aside className="w-full md:w-60 border-b md:border-b-0 md:border-r bg-white p-4 shrink-0 overflow-y-auto">
        <Link to="/esign"
          className="flex items-center gap-2 text-sm text-gray-500 hover:text-teal-700 mb-3">
          <Home className="w-4 h-4" /> eSign home
        </Link>
        <button onClick={() => setNewFlow('choose')}
          className="w-full flex items-center justify-center gap-2 bg-black text-white rounded-lg py-2 text-sm font-medium mb-4">
          <Plus className="w-4 h-4" /> New agreement
        </button>
        <nav className="space-y-0.5">
          <BucketItem label="All agreements" count={counts.all} active={bucket === 'all'} onClick={() => setBucket('all')} bold />
          <div className="pl-2 border-l ml-1 mt-1 space-y-0.5">
            {BUCKETS.filter((b) => b.key !== 'all').map((b) => (
              <BucketItem key={b.key} label={b.label} count={counts[b.key]} active={bucket === b.key} onClick={() => setBucket(b.key)} />
            ))}
          </div>
          <div className="border-t my-3" />
          {['Folders', 'PowerForms', 'Bulk Send'].map((x) => (
            <div key={x} className="flex items-center justify-between px-2 py-1.5 text-sm text-gray-300 cursor-not-allowed">
              <span>{x}</span><span>—</span>
            </div>
          ))}
        </nav>
      </aside>

      {/* Content */}
      <div className="flex-1 overflow-y-auto bg-gray-50">
        <div className="p-6 max-w-5xl mx-auto">
          {/* "All agreements" reads as "My Agreements"/"All Agents Agreements"
              depending on the toggle, so it's clear whose documents you're
              looking at; other buckets (Drafts, In Progress, etc.) keep their
              own label regardless. */}
          <div className="flex items-center justify-between gap-3 mb-1">
            <div className="flex items-center gap-3">
              <FileSignature className="w-6 h-6 text-teal-600" />
              <h1 className="text-2xl font-semibold text-gray-900">
                {bucket === 'all'
                  ? (agencyWide ? 'All Agents Agreements' : 'My Agreements')
                  : BUCKET_TITLE[bucket]}
              </h1>
            </div>
            {canSeeEveryAgent && (
              <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={agencyWide}
                  onChange={(e) => setAgencyWide(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-teal-600 focus:ring-teal-600"
                />
                All agents
              </label>
            )}
          </div>
          <p className="text-sm text-gray-500 mb-5">
            {agencyWide
              ? 'Every agent’s agreements, agency-wide.'
              : 'Send documents for electronic signature.'}
          </p>

          {/* Search + agent filter */}
          <div className="flex items-center gap-3 mb-4">
            <div className="relative flex-1">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input value={query} onChange={(e) => setQuery(e.target.value)}
                placeholder={`Search by title, agent, or recipient`}
                className="w-full border rounded-lg pl-9 pr-3 py-2 text-sm bg-white" />
            </div>
            {agencyWide && (
              <select
                value={agentFilter}
                onChange={(e) => setAgentFilter(e.target.value)}
                className="border rounded-lg px-3 py-2 text-sm bg-white text-gray-700"
              >
                <option value="all">All agents</option>
                {agentOptions.map((name) => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>
            )}
          </div>

          {/* Date range + sort */}
          <div className="flex flex-wrap items-center gap-3 mb-4 text-sm">
            <label className="flex items-center gap-2 text-gray-500">
              From
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                max={dateTo || undefined}
                className="border rounded-lg px-2 py-1.5 bg-white text-gray-700"
              />
            </label>
            <label className="flex items-center gap-2 text-gray-500">
              To
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                min={dateFrom || undefined}
                className="border rounded-lg px-2 py-1.5 bg-white text-gray-700"
              />
            </label>
            {(dateFrom || dateTo) && (
              <button
                type="button"
                onClick={() => { setDateFrom(''); setDateTo(''); }}
                className="text-teal-700 hover:text-teal-800 text-xs font-medium"
              >
                Clear dates
              </button>
            )}
            <select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
              className="ml-auto border rounded-lg px-3 py-1.5 bg-white text-gray-700"
            >
              <option value="desc">Newest to oldest</option>
              <option value="asc">Oldest to newest</option>
            </select>
          </div>

          {/* Table */}
          <div className="bg-white border rounded-xl overflow-visible">
            <div className="grid grid-cols-[minmax(0,1fr)_auto_auto] sm:grid-cols-[minmax(0,2.5fr)_minmax(0,1.4fr)_minmax(0,1.2fr)_auto] gap-2 px-4 py-2 text-[11px] font-medium text-gray-400 border-b bg-gray-50 uppercase tracking-wide">
              <span>Name</span><span>Status</span><span className="hidden sm:block">Last change</span><span></span>
            </div>

            {loading ? (
              <div className="px-4 py-10 text-center text-sm text-gray-400">Loading…</div>
            ) : paginated.length === 0 ? (
              <div className="px-4 py-12 text-center text-sm text-gray-400">
                {bucket === 'deleted' ? 'No deleted agreements.' : bucket === 'void' ? 'No voided agreements.' : 'No agreements found.'}
              </div>
            ) : (
              paginated.map((doc) => (
                <Row key={doc.id} doc={doc}
                  meEmail={meEmail}
                  showOwner={agencyWide}
                  canManageAll={canSeeEveryAgent}
                  busy={busyId === doc.id}
                  menuOpen={menuOpenId === doc.id}
                  onMenu={() => setMenuOpenId(menuOpenId === doc.id ? null : doc.id)}
                  closeMenu={() => setMenuOpenId(null)}
                  onTitle={() => openTitle(doc)}
                  onOpenProperty={doc.related_property ? () => navigate(`/properties/${doc.related_property}`) : null}
                  onSign={() => navigate(`/esign/sign/${doc.my_sign_request.token}`)}
                  onEdit={() => navigate(editHref(doc))}
                  onResend={() => doResend(doc)}
                  onDownload={() => doDownload(doc)}
                  onVoid={() => { setMenuOpenId(null); setModal({ type: 'void', doc }); }}
                  onTemplate={() => { setMenuOpenId(null); setModal({ type: 'template', doc }); }}
                  onAmend={() => doAmend(doc)}
                  onEditDetails={() => { setMenuOpenId(null); setEditDoc(doc); }}
                  onFixEmail={() => { setMenuOpenId(null); setFixEmailDoc(doc); }}
                  onDelete={() => doDelete(doc)}
                />
              ))
            )}
          </div>

          {/* Pagination */}
          {!loading && filtered.length > 0 && (
            <div className="flex items-center justify-between mt-3 text-sm text-gray-500">
              <span>Showing {(page - 1) * PER_PAGE + 1}–{Math.min(page * PER_PAGE, filtered.length)} of {filtered.length}</span>
              <div className="flex items-center gap-2">
                <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)}
                  className="flex items-center gap-1 px-2 py-1 rounded border disabled:opacity-40"><ChevronLeft className="w-4 h-4" />Prev</button>
                <span>Page {page} of {totalPages}</span>
                <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}
                  className="flex items-center gap-1 px-2 py-1 rounded border disabled:opacity-40">Next<ChevronRight className="w-4 h-4" /></button>
              </div>
            </div>
          )}
        </div>
      </div>

      {newFlow === 'choose' && (
        <ChooserModal
          onClose={() => setNewFlow(null)}
          onPick={async (kind) => {
            if (kind === 'cos') {
              try {
                const { data } = await esignApi.createContractOfSale({});
                setNewFlow(null);
                navigate(`/esign/contract-of-sale/${data.id}`);
              } catch {
                toast.error('Could not start the Contract of Sale.');
              }
              return;
            }
            setNewFlow((kind === 'contract' || kind === 'upload') ? kind : `authority:${kind}`);
          }} />
      )}
      {newFlow === 'upload' && (
        <CreateModal
          heading="Upload a document"
          note="Upload any PDF, then place the signature and fill-in fields yourself in the editor."
          noteTone="info"
          onClose={() => setNewFlow(null)}
          onCreated={(id) => { setNewFlow(null); navigate(`/esign/envelope/${id}`); }} />
      )}
      {newFlow === 'contract' && (
        <CreateModal
          heading="Contract of Sale - AI Assist"
          note={contractPrefill?.offer?.id
            // Both paths land on the buyer-details questions next, so pointing
            // the agent at "AI: detect fields" would be wrong.
            ? "Next you get the buyer’s offer questions, already answered from their offer. Check them, then the AI writes them onto the contract."
            : "Next you get the buyer’s offer questions to fill out. The AI then writes those answers onto the contract."}
          showOfferFlow
          initialProperty={contractPrefill?.property || null}
          initialRecipient={contractPrefill?.recipient || null}
          initialOffer={contractPrefill?.offer || null}
          onClose={() => { setNewFlow(null); setContractPrefill(null); }}
          onCreated={(id) => {
            // "Agent to fill out" goes to the buyer-details questions first, the
            // same ones the Buyer Portal asks the buyer (CG, 31 Aug 2026), and
            // Continue from there runs the AI fill on the contract. Coming from
            // an offer, those answers arrive already filled in, so the agent is
            // checking them rather than typing them.
            setNewFlow(null); setContractPrefill(null);
            navigate(`/esign/contract-of-sale/${id}`);
          }} />
      )}
      {typeof newFlow === 'string' && newFlow.startsWith('authority:') && (
        <AuthorityModal
          authorityKey={newFlow.split(':')[1]}
          onClose={() => setNewFlow(null)}
          onCreated={(id) => { setNewFlow(null); navigate(`/esign/authority/${id}`); }} />
      )}
      {modal?.type === 'void' && (
        <ConfirmModal title="Void document"
          body={`A large VOID watermark will be written on every page of "${modal.doc.title}". This cannot be undone.`}
          confirmLabel="Void document" danger busy={busyId === modal.doc.id}
          onConfirm={confirmVoid} onClose={() => setModal(null)} />
      )}
      {modal?.type === 'template' && (
        <ConfirmModal title="Save as template"
          body={`Save "${modal.doc.title}" as a reusable template (its file, recipients and fields are copied).`}
          confirmLabel="Save as template" busy={busyId === modal.doc.id}
          onConfirm={confirmTemplate} onClose={() => setModal(null)} />
      )}
      {fixEmailDoc && (
        <FixEmailModal docId={fixEmailDoc.id} meEmail={meEmail}
          onClose={() => setFixEmailDoc(null)}
          onSaved={() => { setFixEmailDoc(null); load(); }} />
      )}
      {editDoc && (
        <EditDetailsModal docId={editDoc.id}
          onClose={() => setEditDoc(null)}
          onSaved={() => { setEditDoc(null); load(); }} />
      )}
    </div>
  );
}

function BucketItem({ label, count, active, onClick, bold }) {
  return (
    <button onClick={onClick}
      className={`w-full flex items-center justify-between px-2 py-1.5 rounded text-sm ${active ? 'bg-teal-50 text-teal-700' : 'text-gray-600 hover:bg-gray-50'} ${bold ? 'font-medium' : ''}`}>
      <span>{label}</span>
      <span className={`text-xs ${active ? 'text-teal-600' : 'text-gray-400'}`}>{count}</span>
    </button>
  );
}

function Row({ doc, meEmail, showOwner, canManageAll, busy, menuOpen, onMenu, closeMenu, onTitle, onOpenProperty, onSign, onEdit, onResend, onDownload, onVoid, onTemplate, onAmend, onEditDetails, onFixEmail, onDelete }) {
  const isOwner = doc.is_owner;
  const isDraft = doc.status === 'draft';
  const needToSign = doc.my_sign_request && doc.my_sign_request.status !== 'signed';
  const isPending = doc.status === 'pending';
  const isCompleted = doc.status === 'completed';
  const isVoided = doc.status === 'voided';
  const isDeleted = doc.status === 'deleted';
  // order 1 is always the agent's own pre-signature, applied up front as part
  // of sending (see authority_service.instantiate) - it's already 'signed'
  // the INSTANT a doc becomes pending, on every authority, with no exception.
  // Counting it here meant canReopen was permanently false for anything ever
  // sent, blocking edits even when the actual recipient (order >= 2) hadn't
  // touched it yet (CG, 2026-08-06: "Please add a part to edit document" -
  // confirmed live against several real pending authorities). Only a real
  // recipient's signature should lock editing.
  const anySigned = (doc.sign_requests || []).some((sr) => sr.order >= 2 && sr.status === 'signed');
  const canReopen = isPending && isOwner && !anySigned;
  // "Sign" only once the doc is actually SENT (pending). A draft still lists the agent as
  // a signer (authorities/templates seed it up front) but must NOT offer signing yet.
  const showSign = needToSign && isPending;
  // Fields are editable while the doc is a draft, or pending before anyone has signed.
  const canEditFields = isOwner && (isDraft || canReopen);
  // Title, subject, message and recipients can be edited until someone signs.
  const canEditDetails = isOwner && (isDraft || canReopen);
  // A wrong recipient email can be corrected while the document is out for
  // signing, even once a co-signer has signed (ESI: the authority was stuck
  // waiting on a vendor whose email was mistyped, and once the first vendor
  // signed "Edit details" disappeared). This only ever touches a recipient who
  // has NOT signed - the backend returns 409 for one who has - so it never
  // alters the document or anyone's existing signature. Order 1 is the agent's
  // own pre-signature, so an unsigned order >= 2 is a real party still waiting.
  const hasUnsignedSigner = (doc.sign_requests || []).some((sr) => sr.order >= 2 && sr.status !== 'signed');
  const canFixEmail = isOwner && isPending && hasUnsignedSigner;
  // A super admin / dev viewing the agency-wide list can open any agent's
  // document (read-only detail view), not just their own or ones they sign.
  const canOpen = !isDeleted && (isOwner || needToSign || canManageAll);
  const label = statusLabel(doc);
  const openedAt = firstOpenedAt(doc);

  return (
    <div className="grid grid-cols-[minmax(0,1fr)_auto_auto] sm:grid-cols-[minmax(0,2.5fr)_minmax(0,1.4fr)_minmax(0,1.2fr)_auto] gap-2 px-4 py-3 border-b items-center hover:bg-gray-50">
      {/* Name + recipients */}
      <div className="min-w-0">
        <button onClick={canOpen ? onTitle : undefined}
          className={`block w-full text-left font-medium truncate ${canOpen ? 'text-gray-900 hover:text-teal-700' : 'text-gray-500 cursor-default'}`}>
          {doc.title}
          {showOwner && doc.owner_name && (
            <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-teal-50 text-teal-700 align-middle">
              {doc.owner_name}
            </span>
          )}
        </button>
        <div className="flex items-center gap-2 text-xs text-gray-400 truncate">
          <span className="truncate">{recipientsLine(doc, meEmail)}</span>
          {onOpenProperty && (
            <button type="button" onClick={onOpenProperty} title="Open property"
              className="inline-flex items-center gap-0.5 shrink-0 text-teal-600 hover:text-teal-800">
              <Home className="w-3 h-3" /> Property
            </button>
          )}
        </div>
      </div>

      {/* Status */}
      <div className="min-w-0">
        <span className={`inline-flex max-w-full items-center gap-1 px-2 py-0.5 rounded text-xs font-medium truncate ${pillClass(doc)}`}>
          {isCompleted && <Check className="w-3 h-3 shrink-0" />}<span className="truncate">{label}</span>
        </span>
        {/* Opened indicator (CG): show whether a recipient has opened the
            document and when, not just on the dashboard. */}
        {openedAt ? (
          <span className="mt-0.5 flex items-center gap-1 text-[11px] text-emerald-600" title={`First opened ${fmt(openedAt)}`}>
            <Eye className="w-3 h-3 shrink-0" /> Opened {fmt(openedAt)}
          </span>
        ) : (!isDraft && !isDeleted && label !== 'Generated') ? (
          <span className="mt-0.5 flex items-center gap-1 text-[11px] text-gray-400">
            <Eye className="w-3 h-3 shrink-0" /> Not opened yet
          </span>
        ) : null}
      </div>

      {/* Last change */}
      <div className="hidden sm:block text-sm text-gray-500">{fmt(doc.updated_at || doc.created_at)}</div>

      {/* Actions */}
      <div className="flex items-center gap-1 justify-end relative">
        {!isDeleted && (
          <>
            {showSign && (
              <button onClick={onSign} className="px-3 py-1.5 rounded bg-teal-600 text-white text-xs font-medium">Sign</button>
            )}
            {canEditFields && !showSign && (
              <button onClick={onEdit} title="Edit fields" className="p-1.5 rounded hover:bg-gray-100 text-gray-500"><Pencil className="w-4 h-4" /></button>
            )}
            {isPending && isOwner && !needToSign && (
              <button onClick={onResend} disabled={busy} title="Resend" className="p-1.5 rounded hover:bg-gray-100 text-gray-500"><Send className="w-4 h-4" /></button>
            )}
            {(isPending || isCompleted || isVoided) && (
              <button onClick={onDownload} title="Download" className="p-1.5 rounded hover:bg-gray-100 text-gray-500"><Download className="w-4 h-4" /></button>
            )}

            {/* Kebab */}
            <button onClick={onMenu} className="p-1.5 rounded hover:bg-gray-100 text-gray-500"><MoreVertical className="w-4 h-4" /></button>
            {menuOpen && (
              <>
                <div className="fixed inset-0 z-30" onClick={closeMenu} />
                <div className="absolute right-0 top-9 z-40 bg-white border rounded-lg shadow-lg py-1 w-44 text-sm">
                  {canEditDetails && (
                    <button onClick={onEditDetails} className="w-full text-left px-3 py-2 hover:bg-gray-50">Edit details</button>
                  )}
                  {canFixEmail && (
                    <button onClick={onFixEmail} className="w-full text-left px-3 py-2 hover:bg-gray-50">Fix email &amp; resend</button>
                  )}
                  <button onClick={onTemplate} className="w-full text-left px-3 py-2 hover:bg-gray-50">Save as template</button>
                  {doc.can_amend && (
                    <button onClick={onAmend} className="w-full text-left px-3 py-2 hover:bg-gray-50">Amend</button>
                  )}
                  {isOwner && !isVoided && (
                    <button onClick={onVoid} className="w-full text-left px-3 py-2 hover:bg-gray-50">Void</button>
                  )}
                  {(isOwner || canManageAll) && (
                    <button onClick={onDelete} className="w-full text-left px-3 py-2 hover:bg-red-50 text-red-600">Delete</button>
                  )}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function ConfirmModal({ title, body, confirmLabel, danger, busy, onConfirm, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl w-full max-w-md p-6 shadow-xl">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold">{title}</h3>
          <button onClick={onClose}><X className="w-5 h-5 text-gray-500" /></button>
        </div>
        <p className="text-sm text-gray-600 mb-5">{body}</p>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm rounded border">Cancel</button>
          <button onClick={onConfirm} disabled={busy}
            className={`px-4 py-2 text-sm rounded text-white disabled:opacity-50 ${danger ? 'bg-red-600' : 'bg-black'}`}>
            {busy ? 'Working…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// Recipient row with a user-suggestion dropdown (filtered from CRM users); typing a
// free-text external email still works.
function RecipientRow({ r, signingOrder, canRemove, onChange, onRemove }) {
  const [open, setOpen] = useState(false);
  const [matches, setMatches] = useState([]);
  const timer = useRef();

  // Debounced server-side search over ALL active users (CRM staff + portal users).
  const search = useCallback((q) => {
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try {
        const { data } = await esignApi.recipientSuggestions(q || '');
        setMatches(Array.isArray(data) ? data : []);
      } catch { setMatches([]); }
    }, 200);
  }, []);

  useEffect(() => { search(''); return () => clearTimeout(timer.current); }, [search]);

  const onType = (patch, q) => { onChange(patch); setOpen(true); search(q); };
  const pick = (u) => { onChange({ name: cleanPersonName(u.name), email: u.email }); setOpen(false); };
  const closeSoon = () => setTimeout(() => setOpen(false), 150);

  return (
    <div className="flex gap-2 mb-2 items-start">
      {/* name + email share one suggestion dropdown */}
      <div className="relative flex gap-2 flex-1">
        <input value={r.name}
          onChange={(e) => onType({ name: e.target.value }, e.target.value)}
          onFocus={() => setOpen(true)} onBlur={closeSoon}
          placeholder="Name" className="border rounded px-2 py-1.5 text-sm w-2/5" />
        <input value={r.email}
          onChange={(e) => onType({ email: e.target.value }, e.target.value)}
          onFocus={() => setOpen(true)} onBlur={closeSoon}
          placeholder="Email or search users" className="border rounded px-2 py-1.5 text-sm flex-1" />
        {open && matches.length > 0 && (
          <div className="absolute z-50 left-0 right-0 top-full mt-1 bg-white border rounded-lg shadow-lg max-h-52 overflow-y-auto">
            {matches.map((u, idx) => (
              <button type="button" key={idx} onMouseDown={(e) => e.preventDefault()} onClick={() => pick(u)}
                className="w-full text-left px-3 py-2 hover:bg-gray-50 text-sm">
                <div className="font-medium text-gray-800 truncate">{u.name || u.email}</div>
                {u.name ? <div className="text-xs text-gray-400 truncate">{u.email}</div> : null}
              </button>
            ))}
          </div>
        )}
      </div>
      <select value={r.role} onChange={(e) => onChange({ role: e.target.value })} className="border rounded px-2 py-1.5 text-sm">
        <option value="signer">Signer</option>
        <option value="approver">Approver</option>
        <option value="cc">CC</option>
        <option value="viewer">Viewer</option>
      </select>
      {signingOrder && (
        <input type="number" min={1} value={r.order} onChange={(e) => onChange({ order: parseInt(e.target.value) || 1 })}
          className="border rounded px-2 py-1.5 text-sm w-14" />
      )}
      {canRemove && (
        <button type="button" onClick={onRemove} className="text-gray-400 mt-1.5"><X className="w-4 h-4" /></button>
      )}
    </div>
  );
}

// Plain-English summary of what the buyer's offer will put on the contract, so
// the agent can see the figures before they are carried across.
function offerSummary(offer) {
  if (!offer) return [];
  const money = (v) => {
    const n = Number(v);
    return Number.isFinite(n) && n > 0 ? `$${Math.round(n).toLocaleString()}` : '';
  };
  const price = money(offer.offer_amount)
    || (offer.offer_amount_min && offer.offer_amount_min === offer.offer_amount_max
      ? money(offer.offer_amount_min) : '');
  const out = [];
  if (price) out.push(`Offer ${price}`);
  if (money(offer.deposit_amount)) out.push(`deposit ${money(offer.deposit_amount)}`);
  if (offer.custom_settlement_date) out.push(`settles ${offer.custom_settlement_date}`);
  else if (offer.settlement_days) out.push(`${offer.settlement_days} day settlement`);
  return out;
}

// A name in the case a person writes it in ("LISA SIMMS" -> "Lisa Simms").
const toPascalCase = (str) =>
  (!str || typeof str !== 'string')
    ? ''
    : str.trim().split(/\s+/)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');

// Buyer's first name only ("LISA Simms" -> "Lisa").
const firstNameOf = (full) => toPascalCase(String(full || '').trim().split(/\s+/)[0] || '');

const asMoney = (v) => {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? `$${Math.round(n).toLocaleString()}` : '';
};

const settlementTerms = (offer) => {
  if (!offer) return '';
  if (offer.custom_settlement_date) {
    return formatMelbourneDayLabel(offer.custom_settlement_date)
      || String(offer.custom_settlement_date);
  }
  return offer.settlement_days ? `${offer.settlement_days} days` : '';
};

/**
 * The default subject and message for a Contract of Sale going to the buyer who
 * made the offer, with the offer's own figures already written in.
 *
 * CG, 28 Aug 2026 (Monday 12450978564): "Please add an auto email Subject &
 * message... I'd make the default message very clean and transactional, because
 * this is the buyer signing their offer/Contract of Sale, not a marketing
 * email." Anything we do not know is left out rather than sent as an empty
 * label, and the agent can still edit both fields before sending.
 */
function contractEmailDefaults({ property, recipient, offer, agent }) {
  const address = property?.address || property?.title || '';
  const buyer = firstNameOf(recipient?.name);
  const price = asMoney(offer?.offer_amount)
    || (offer?.offer_amount_min && offer.offer_amount_min === offer.offer_amount_max
      ? asMoney(offer.offer_amount_min) : '');
  const deposit = asMoney(offer?.deposit_amount);
  const settlement = settlementTerms(offer);
  const agentName = agent?.name ? toPascalCase(agent.name) : '';
  const agentMobile = agent?.phone_number || '';

  const figures = [
    price && `Offer: ${price}`,
    deposit && `Deposit: ${deposit}`,
    settlement && `Settlement: ${settlement}`,
  ].filter(Boolean);

  // Without a mobile on file, naming the agent and asking the buyer to ring a
  // blank number is worse than pointing them at the office.
  const contactLine = agentName && agentMobile
    ? `If anything needs to be changed before signing, please contact ${agentName} on ${agentMobile}.`
    : 'If anything needs to be changed before signing, please contact your Collings agent.';

  const lines = [
    buyer ? `Hi ${buyer},` : 'Hello,',
    '',
    `Please find the Contract of Sale${address ? ` for ${address}` : ''}, prepared with the details of your offer.`,
  ];
  if (figures.length) lines.push('', ...figures);
  lines.push(
    '',
    'Please review all details carefully and follow the link to complete and sign the contract.',
    '',
    contactLine,
    '',
    'Kind regards,',
    ...(agentName ? [agentName] : []),
    'Collings Real Estate',
  );

  return {
    subject: `Contract of Sale for Signing${address ? `: ${address}` : ''}`,
    message: lines.join('\n'),
  };
}

// ---- New document modal (upload + recipients -> editor) ----
function CreateModal({
  onClose, onCreated, heading = 'New document', note = '', noteTone = 'ai',
  showOfferFlow = false, initialProperty = null, initialRecipient = null,
  initialOffer = null,
}) {
  const agent = useSelector((s) => s.auth?.user);
  // Started from an offer we already hold, as opposed to the agent opening this
  // screen to get a contract signed before any offer exists. The difference
  // decides both the default email and whether asking the buyer for an offer
  // still makes sense (CG, 28 Aug 2026: "remove the send offer link to buyer
  // button as we have already gotten the offer").
  const fromOffer = !!initialOffer?.id;
  const defaults = useMemo(
    () => (fromOffer
      ? contractEmailDefaults({
        property: initialProperty, recipient: initialRecipient, offer: initialOffer, agent,
      })
      : { subject: '', message: '' }),
    // Recomputed on property change below; the deps here are the mount values.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fromOffer],
  );

  const [title, setTitle] = useState('');
  const [file, setFile] = useState(null);
  const [subject, setSubject] = useState(defaults.subject);
  const [message, setMessage] = useState(defaults.message);
  // Once the agent types in either field we stop rewriting it underneath them.
  const [emailEdited, setEmailEdited] = useState(false);
  const [signingOrder, setSigningOrder] = useState(false);
  const [recipients, setRecipients] = useState(
    initialRecipient ? [{ ...initialRecipient }] : [{ name: '', email: '', role: 'signer', order: 1 }]
  );
  const [saving, setSaving] = useState(false);
  const [sendingOfferLink, setSendingOfferLink] = useState(false);

  // Property link (offer flow only) — selecting a property auto-loads its
  // most-recently uploaded Contract of Sale, if one's already on file, so
  // the agent doesn't have to re-upload a contract that's already in the CRM.
  const [property, setProperty] = useState(initialProperty);
  const [existingDoc, setExistingDoc] = useState(null); // null = not checked yet, true/false once checked

  const searchProps = useCallback(async (text) => {
    if (!text || text.trim().length < 2) return [];
    const { data } = await searchProperties({ search: text, page_size: 8 });
    return Array.isArray(data) ? data : (data.results || data.properties || []);
  }, []);

  const checkExistingDoc = useCallback(async (prop) => {
    if (!prop) { setExistingDoc(null); return; }
    try {
      const { data } = await getPropertyDocuments(prop.id);
      const docs = Array.isArray(data) ? data : (data?.results || []);
      setExistingDoc(docs.some((d) => d.category === 'contract_docs'));
    } catch {
      setExistingDoc(false);
    }
  }, []);

  useEffect(() => {
    if (initialProperty) {
      checkExistingDoc(initialProperty);
      if (showOfferFlow) {
        setTitle(initialProperty.address || initialProperty.title || `Property #${initialProperty.id}`);
      }
    }
    // Only run once on mount for the prefill case — later property picks are
    // handled by pickProperty below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pickProperty = (p) => {
    setProperty(p);
    checkExistingDoc(p);
    // A contract dropped in for the previous property must never follow the
    // agent onto a different one - it would be filed against the wrong address.
    setFile(null);
    // Property Contract of Sale has no separate Title field (it's just the address) —
    // derive it here so the submit payload still has one.
    if (showOfferFlow) setTitle(p.address || p.title || `Property #${p.id}`);
    // The address is written into the default subject and message, so a different
    // property means a different email, unless the agent has written their own.
    if (fromOffer && !emailEdited) {
      const next = contractEmailDefaults({
        property: p, recipient: initialRecipient, offer: initialOffer, agent,
      });
      setSubject(next.subject);
      setMessage(next.message);
    }
    // Recipients here are the BUYER this offer/contract is going to, never
    // the property's owner (vendor) - this screen previously prefilled the
    // recipient list from the property's owner on pick, so an agent who
    // didn't notice and overwrite it sent the buyer's contract to the
    // vendor instead (Paul Jimoulis, ESI-2026-00002: "I don't think they
    // have received it"). No owner prefill here; the buyer is either
    // already set via initialRecipient (deep-linked from an existing Offer)
    // or the agent types them in directly.
  };
  const clearProperty = () => {
    setProperty(null);
    setExistingDoc(null);
    setFile(null);
  };

  const addRecipient = () =>
    setRecipients((r) => [...r, { name: '', email: '', role: 'signer', order: r.length + 1 }]);
  const updateRecipient = (i, key, val) =>
    setRecipients((r) => r.map((x, idx) => (idx === i ? { ...x, [key]: val } : x)));
  const removeRecipient = (i) => setRecipients((r) => r.filter((_, idx) => idx !== i));

  const submit = async () => {
    if (!title.trim()) return toast.error('Add a title.');
    if (!file && !(property && existingDoc)) return toast.error('Upload a PDF.');
    const valid = dedupeRecipients(recipients);
    if (!valid.length) return toast.error('Add at least one recipient.');

    const fd = new FormData();
    fd.append('title', title);
    if (file) fd.append('original_file', file);
    if (property) fd.append('related_property', property.id);
    // The buyer's offer this contract is being written up from - the backend
    // carries its figures onto the contract's buyer sheet.
    if (initialOffer?.id) fd.append('offer_id', initialOffer.id);
    // Mark it a Contract of Sale even with no offer behind it (one taken over
    // the phone), so the agent still gets the buyer-details questions.
    if (showOfferFlow) fd.append('doc_kind', 'contract_of_sale');
    fd.append('subject', subject);
    fd.append('message', message);
    fd.append('signing_order', signingOrder);
    fd.set('recipients', JSON.stringify(valid));

    setSaving(true);
    try {
      const { data } = await esignApi.createEnvelope(fd);
      toast.success('Document created.');
      onCreated(data.id);
    } catch (e) {
      toast.error(e?.response?.data?.original_file || 'Create failed.');
    } finally {
      setSaving(false);
    }
  };

  const sendOfferLink = async () => {
    if (!property) return toast.error('Select a property.');
    // Every recipient with an email gets their own invite + email - a
    // previous version only ever sent to the first one (Paul Jimoulis,
    // ESI-2026-00006: "the second recipient does not receive the email").
    const targets = dedupeRecipients(recipients);
    if (!targets.length) return toast.error('Add the buyer’s name and email as a recipient.');

    setSendingOfferLink(true);
    try {
      const results = await Promise.allSettled(
        targets.map((r) => esignApi.sendOfferInvite(property.id, { name: r.name, email: r.email, message }))
      );
      const failures = results.filter((r) => r.status === 'rejected');

      if (!failures.length) {
        toast.success(targets.length > 1 ? `Offer link sent to ${targets.length} buyers.` : 'Offer link sent to buyer.');
        onClose();
      } else if (failures.length < targets.length) {
        toast.error(`Sent to ${targets.length - failures.length} of ${targets.length} buyers - one or more failed. Try again for the rest.`);
      } else {
        toast.error(failures[0]?.reason?.response?.data?.detail || 'Could not send the offer link.');
      }
    } finally {
      setSendingOfferLink(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl w-full max-w-lg p-6 shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">{heading}</h3>
          <button onClick={onClose}><X className="w-5 h-5 text-gray-500" /></button>
        </div>
        {note && (
          noteTone === 'info' ? (
            <div className="flex items-start gap-2 text-xs text-teal-700 bg-teal-50 border border-teal-200 rounded-lg px-3 py-2 mb-4">
              <FileText className="w-4 h-4 shrink-0 mt-0.5" /><span>{note}</span>
            </div>
          ) : (
            <div className="flex items-start gap-2 text-xs text-violet-700 bg-violet-50 border border-violet-200 rounded-lg px-3 py-2 mb-4">
              <Sparkles className="w-4 h-4 shrink-0 mt-0.5" /><span>{note}</span>
            </div>
          )
        )}

        {showOfferFlow && (
          <DealPicker
            label="Property" placeholder="Search properties by address"
            search={searchProps} value={property}
            render={(p) => p.address || p.title || `Property #${p.id}`}
            onPick={pickProperty} onClear={clearProperty} optional={false} />
        )}

        {!showOfferFlow && (
          <>
            <label className="block text-sm font-medium mb-1">Title</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)}
              className="w-full border rounded px-3 py-2 mb-3" placeholder="e.g. Sale Contract, 12 Smith St" />
          </>
        )}

        {showOfferFlow ? (
          // Search-first: this screen finds and links a property's existing
          // Contract of Sale. It stays search-only while one IS on file - a
          // genuinely new contract PDF belongs on the "Upload your own PDF"
          // tile (CG, 2026-08-13: "Remove PDF file upload. PDF file upload is
          // for the other tab link"). The upload below only appears as the way
          // out of the dead end when the property has NO contract on file
          // (CG, 2026-08-16: "the contract isnt popping up. Can there be a
          // button that allows the agent to drop it in?").
          property && existingDoc && (
            <div className="mb-3 flex items-start gap-2 text-xs text-teal-700 bg-teal-50 border border-teal-200 rounded-lg px-3 py-2">
              <FileText className="w-4 h-4 shrink-0 mt-0.5" />
              <span>Using existing Contract of Sale for this property.</span>
            </div>
          )
        ) : (
          <>
            <label className="block text-sm font-medium mb-1">PDF file</label>
            <input type="file" accept="application/pdf" onChange={(e) => setFile(e.target.files[0])}
              className="w-full border rounded px-3 py-2 mb-3 text-sm" />
          </>
        )}
        {showOfferFlow && property && existingDoc === false && (
          <div className="mb-3 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2.5">
            <div className="flex items-start gap-2 text-xs text-amber-800 mb-2">
              <FileText className="w-4 h-4 shrink-0 mt-0.5" />
              <span>
                No Contract of Sale on file for this property yet. Drop the contract PDF in
                here and it is saved against the property, so it is on file next time.
              </span>
            </div>
            <label className="inline-flex items-center gap-2 rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-sm font-medium text-amber-800 cursor-pointer hover:bg-amber-100">
              <Upload className="w-4 h-4" />
              {file ? 'Choose a different PDF' : 'Upload contract PDF'}
              <input type="file" accept="application/pdf" className="hidden"
                onChange={(e) => setFile(e.target.files[0] || null)} />
            </label>
            {file && <span className="ml-2 text-xs text-amber-800">{file.name}</span>}
          </div>
        )}
        {showOfferFlow && offerSummary(initialOffer).length > 0 && (
          <div className="mb-3 flex items-start gap-2 text-xs text-teal-700 bg-teal-50 border border-teal-200 rounded-lg px-3 py-2">
            <Sparkles className="w-4 h-4 shrink-0 mt-0.5" />
            <span>
              The buyer's offer ({offerSummary(initialOffer).join(', ')}) is filled onto the
              contract for you. Check every figure before sending.
            </span>
          </div>
        )}

        <label htmlFor="esign-subject" className="block text-sm font-medium mb-1">
          Email subject
        </label>
        <input id="esign-subject" value={subject}
          onChange={(e) => { setEmailEdited(true); setSubject(e.target.value); }}
          className="w-full border rounded px-3 py-2 mb-3" />

        <label htmlFor="esign-message" className="block text-sm font-medium mb-1">
          Message to recipients
        </label>
        <textarea id="esign-message" value={message}
          onChange={(e) => { setEmailEdited(true); setMessage(e.target.value); }}
          rows={fromOffer ? 12 : 2} className="w-full border rounded px-3 py-2 mb-3" />

        <div className="flex items-center justify-between mb-2">
          <label className="text-sm font-medium">Recipients</label>
          <label className="flex items-center gap-2 text-xs text-gray-500">
            <input type="checkbox" checked={signingOrder} onChange={(e) => setSigningOrder(e.target.checked)} />
            Sign in order
          </label>
        </div>
        {recipients.map((r, i) => (
          <RecipientRow key={i} r={r} signingOrder={signingOrder}
            canRemove={recipients.length > 1}
            onChange={(patch) => setRecipients((rs) => rs.map((x, idx) => (idx === i ? { ...x, ...patch } : x)))}
            onRemove={() => removeRecipient(i)} />
        ))}
        <button onClick={addRecipient} className="text-sm text-teal-600 mb-4">+ Add recipient</button>

        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm rounded border">Cancel</button>
          {/* Asking the buyer to put an offer in only makes sense when we do not
              already have one. Arriving here from their offer, the next step is
              the contract itself (CG, 28 Aug 2026). */}
          {showOfferFlow && !fromOffer && (
            <button onClick={sendOfferLink} disabled={sendingOfferLink || !property}
              className="px-4 py-2 text-sm rounded bg-teal-600 text-white disabled:opacity-50">
              {sendingOfferLink ? 'Sending…' : 'Send offer link to buyer'}
            </button>
          )}
          <button onClick={submit} disabled={saving || (showOfferFlow && !(property && (existingDoc || file)))}
            className="px-4 py-2 text-sm rounded bg-black text-white disabled:opacity-50">
            {saving ? 'Creating…' : (showOfferFlow ? 'Agent to fill out' : 'Create & place fields')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---- Edit details modal (draft only): title/subject/message + recipients ----
// Lets the owner change the envelope metadata and signer list before anyone signs,
// without going into the field editor. The PDF itself is not replaced here.
function EditDetailsModal({ docId, onClose, onSaved }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [title, setTitle] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [signingOrder, setSigningOrder] = useState(false);
  const [recipients, setRecipients] = useState([]);
  const [original, setOriginal] = useState([]);

  useEffect(() => {
    let live = true;
    esignApi.getEnvelope(docId)
      .then(({ data }) => {
        if (!live) return;
        setTitle(data.title || '');
        setSubject(data.subject || '');
        setMessage(data.message || '');
        setSigningOrder(!!data.signing_order);
        const recs = (data.recipients || []).map((r) => ({
          id: r.id, name: r.name || '', email: r.email || '', role: r.role || 'signer', order: r.order || 1,
        }));
        setRecipients(recs.length ? recs : [{ name: '', email: '', role: 'signer', order: 1 }]);
        setOriginal(recs);
      })
      .catch(() => toast.error('Could not load the document details.'))
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [docId]);

  const addRecipient = () =>
    setRecipients((r) => [...r, { name: '', email: '', role: 'signer', order: r.length + 1 }]);
  const removeRow = (i) => setRecipients((r) => r.filter((_, idx) => idx !== i));

  const submit = async () => {
    if (!title.trim()) return toast.error('Add a title.');
    const valid = dedupeRecipients(recipients);
    if (!valid.length) return toast.error('Add at least one recipient.');

    setSaving(true);
    try {
      await esignApi.updateEnvelope(docId, {
        title, subject, message, signing_order: signingOrder,
      });
      // Recipient diff vs the loaded snapshot.
      const keptIds = new Set(valid.filter((r) => r.id).map((r) => r.id));
      for (const o of original) {
        if (!keptIds.has(o.id)) await esignApi.removeRecipient(docId, o.id);
      }
      for (const r of valid) {
        const payload = { name: r.name, email: r.email, role: r.role, order: r.order };
        if (!r.id) {
          await esignApi.addRecipient(docId, payload);
        } else {
          const o = original.find((x) => x.id === r.id);
          if (o && (o.name !== r.name || o.email !== r.email || o.role !== r.role || o.order !== r.order)) {
            await esignApi.updateRecipient(docId, r.id, payload);
          }
        }
      }
      toast.success('Details updated.');
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Could not save changes.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl w-full max-w-lg p-6 shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Edit details</h3>
          <button onClick={onClose}><X className="w-5 h-5 text-gray-500" /></button>
        </div>

        {loading ? (
          <div className="py-10 text-center text-sm text-gray-400">Loading…</div>
        ) : (
          <>
            <label className="block text-sm font-medium mb-1">Title</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)}
              className="w-full border rounded px-3 py-2 mb-3" placeholder="e.g. Sale Contract, 12 Smith St" />

            <label className="block text-sm font-medium mb-1">Email subject</label>
            <input value={subject} onChange={(e) => setSubject(e.target.value)} className="w-full border rounded px-3 py-2 mb-3" />

            <label className="block text-sm font-medium mb-1">Message to recipients</label>
            <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={2} className="w-full border rounded px-3 py-2 mb-3" />

            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium">Recipients</label>
              <label className="flex items-center gap-2 text-xs text-gray-500">
                <input type="checkbox" checked={signingOrder} onChange={(e) => setSigningOrder(e.target.checked)} />
                Sign in order
              </label>
            </div>
            {recipients.map((r, i) => (
              <RecipientRow key={r.id ?? `new-${i}`} r={r} signingOrder={signingOrder}
                canRemove={recipients.length > 1}
                onChange={(patch) => setRecipients((rs) => rs.map((x, idx) => (idx === i ? { ...x, ...patch } : x)))}
                onRemove={() => removeRow(i)} />
            ))}
            <button onClick={addRecipient} className="text-sm text-teal-600 mb-4">+ Add recipient</button>

            <div className="flex justify-end gap-2">
              <button onClick={onClose} className="px-4 py-2 text-sm rounded border">Cancel</button>
              <button onClick={submit} disabled={saving} className="px-4 py-2 text-sm rounded bg-black text-white disabled:opacity-50">
                {saving ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ---- Fix recipient email modal (sent doc): correct a mistyped email on a
// recipient who has not signed yet, then the agent hits Resend to re-email the
// corrected address. Unlike "Edit details" this stays available after a
// co-signer has signed, because it only ever changes a still-unsigned party -
// the backend (recipient_detail PATCH) returns 409 for one who has signed, so
// no document content and no existing signature is ever touched.
function FixEmailModal({ docId, meEmail = '', onClose, onSaved }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rows, setRows] = useState([]); // editable: unsigned, non-agent signers
  const [original, setOriginal] = useState([]);

  useEffect(() => {
    let live = true;
    esignApi.getEnvelope(docId)
      .then(({ data }) => {
        if (!live) return;
        const editable = unsignedEditableRecipients(data, meEmail);
        setRows(editable);
        setOriginal(editable.map((r) => ({ ...r })));
      })
      .catch(() => toast.error('Could not load the recipients.'))
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [docId, meEmail]);

  const update = (i, patch) => setRows((rs) => rs.map((x, idx) => (idx === i ? { ...x, ...patch } : x)));

  const submit = async () => {
    // Only the rows whose email or name actually changed are sent.
    const changed = rows.filter((r) => {
      const o = original.find((x) => x.id === r.id);
      return o && ((o.email || '').trim() !== (r.email || '').trim() || (o.name || '').trim() !== (r.name || '').trim());
    });
    if (!changed.length) return toast.error('Change an email (or name) first.');
    for (const r of changed) {
      const email = (r.email || '').trim();
      if (!email || !/^\S+@\S+\.\S+$/.test(email)) {
        return toast.error(`Enter a valid email for ${r.name || 'this recipient'}.`);
      }
    }
    setSaving(true);
    try {
      for (const r of changed) {
        await esignApi.updateRecipient(docId, r.id, { name: r.name, email: r.email.trim(), role: r.role, order: r.order });
      }
      toast.success('Email updated. Now click Resend on this agreement to email the corrected address.');
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Could not update the email.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl w-full max-w-lg p-6 shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg font-semibold">Fix email &amp; resend</h3>
          <button onClick={onClose}><X className="w-5 h-5 text-gray-500" /></button>
        </div>
        <p className="text-sm text-gray-500 mb-4">
          Correct a mistyped email for a recipient who hasn&apos;t signed yet. Save, then click the
          Resend button on the agreement to email the corrected address. Anyone who has already
          signed is left untouched.
        </p>

        {loading ? (
          <div className="py-10 text-center text-sm text-gray-400">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="py-6 text-center text-sm text-gray-500">
            There are no unsigned recipients to correct on this agreement.
          </div>
        ) : (
          <>
            {rows.map((r, i) => (
              <div key={r.id} className="mb-3">
                <label className="block text-xs font-medium text-gray-500 mb-1">Name</label>
                <input value={r.name} onChange={(e) => update(i, { name: e.target.value })}
                  className="w-full border rounded px-3 py-2 mb-2" placeholder="Recipient name" />
                <label className="block text-xs font-medium text-gray-500 mb-1">Email</label>
                <input type="email" value={r.email} onChange={(e) => update(i, { email: e.target.value })}
                  className="w-full border rounded px-3 py-2" placeholder="name@example.com" />
              </div>
            ))}
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={onClose} className="px-4 py-2 text-sm rounded border">Cancel</button>
              <button onClick={submit} disabled={saving} className="px-4 py-2 text-sm rounded bg-black text-white disabled:opacity-50">
                {saving ? 'Saving…' : 'Save email'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ---- Document-type chooser (authorities + contract of sale) ----
function ChooserModal({ onClose, onPick }) {
  const [authorities, setAuthorities] = useState([]);
  const [downloadingKey, setDownloadingKey] = useState(null);
  useEffect(() => {
    let live = true;
    esignApi.listAuthorities()
      .then(({ data }) => { if (live) setAuthorities(Array.isArray(data) ? data : []); })
      .catch(() => {});
    return () => { live = false; };
  }, []);

  // Download a pristine blank copy of an authority (to print / fill by hand)
  // without starting a signing flow. The tile body still opens the flow.
  const downloadBlank = async (a) => {
    setDownloadingKey(a.key);
    try {
      const res = await esignApi.downloadAuthorityBlank(a.key);
      const url = URL.createObjectURL(res.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${a.name}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Could not download the blank form.');
    } finally {
      setDownloadingKey(null);
    }
  };

  // The outer row is a div (not a button) so an optional download button can sit
  // beside the main action without nesting one button inside another.
  const Tile = ({ icon: Icon, title, desc, badge, onClick, onDownload, downloading }) => (
    <div className="w-full flex items-center gap-3 border rounded-xl px-4 py-3 hover:border-teal-400 hover:bg-teal-50/40 transition">
      <button onClick={onClick} className="flex items-center gap-3 text-left min-w-0 flex-1">
        <span className="shrink-0 w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center text-gray-600">
          <Icon className="w-5 h-5" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-2 font-medium text-gray-900">
            {title}
            {badge && <span className="text-[10px] uppercase tracking-wide bg-violet-100 text-violet-700 rounded px-1.5 py-0.5">{badge}</span>}
          </span>
          <span className="block text-xs text-gray-500 truncate">{desc}</span>
        </span>
      </button>
      {onDownload && (
        <button type="button" onClick={onDownload} disabled={downloading}
          title="Download a blank copy to print or fill in by hand"
          className="shrink-0 p-1.5 rounded-lg text-gray-400 hover:text-teal-600 hover:bg-teal-50 disabled:opacity-50">
          <Download className="w-4 h-4" />
        </button>
      )}
      <ChevronRight className="w-4 h-4 text-gray-300 shrink-0" />
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl w-full max-w-lg p-6 shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-lg font-semibold">New agreement</h3>
          <button onClick={onClose}><X className="w-5 h-5 text-gray-500" /></button>
        </div>
        <p className="text-sm text-gray-500 mb-4">Pick a templated authority, upload your own PDF to sign, or upload a Contract of Sale for AI field detection.</p>

        <div className="space-y-2">
          <p className="text-xs font-semibold text-gray-400 uppercase">Templated authorities</p>
          {authorities.length === 0 && (
            <p className="text-xs text-gray-400">Loading authorities…</p>
          )}
          {authorities.map((a) => (
            <Tile key={a.key} icon={AUTHORITY_ICONS[a.key] || ScrollText} title={a.name} desc={a.description}
              onClick={() => onPick(a.key)}
              onDownload={() => downloadBlank(a)}
              downloading={downloadingKey === a.key} />
          ))}

          {/* CG, 31 Aug 2026: the contract is the thing agents come here for, so
              it is named and listed first, above the blank-PDF fallback. */}
          <p className="text-xs font-semibold text-gray-400 uppercase pt-2">Contract of Sale</p>
          <Tile icon={Sparkles} title="Contract of Sale - AI Assist" badge="AI"
            desc="Search Property Address. AI finds the fill-in fields and signature blocks."
            onClick={() => onPick('contract')} />
          <Tile icon={Upload} title="Upload your own PDF"
            desc="Upload any document and place the signature and fill-in fields yourself."
            onClick={() => onPick('upload')} />
        </div>
      </div>
    </div>
  );
}

// ---- Deal typeahead (contacts / properties) for authority auto-fill ----
function DealPicker({ label, placeholder, search, render, value, onPick, onClear, optional = true, onQueryChange }) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [matches, setMatches] = useState([]);
  const [searched, setSearched] = useState(false);
  const timer = useRef();

  const run = useCallback((text) => {
    clearTimeout(timer.current);
    setSearched(false);
    timer.current = setTimeout(async () => {
      try {
        const items = await search(text);
        setMatches(items || []);
      } catch { setMatches([]); }
      setSearched(true);
    }, 250);
  }, [search]);

  if (value) {
    return (
      <div className="mb-3">
        <label className="block text-sm font-medium mb-1">{label}</label>
        <div className="flex items-center justify-between border rounded px-3 py-2 bg-gray-50 text-sm">
          <span className="truncate">{render(value)}</span>
          <button onClick={onClear} className="text-gray-400 ml-2"><X className="w-4 h-4" /></button>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-3 relative">
      <label className="block text-sm font-medium mb-1">
        {label} {optional && <span className="text-gray-400 font-normal">(optional)</span>}
      </label>
      <input value={q} placeholder={placeholder}
        onChange={(e) => { setQ(e.target.value); setOpen(true); run(e.target.value); onQueryChange?.(e.target.value); }}
        onFocus={() => { setOpen(true); run(q); }}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onKeyDown={(e) => {
          // Typing a full address and hitting Enter felt like "submitting" it,
          // but nothing was actually selected - onPick only ever fired from a
          // dropdown row click, so the button below stayed silently disabled
          // with no explanation (CG report, 2026-08-06). Enter now picks the
          // top match, same as a normal address autocomplete.
          if (e.key === 'Enter' && matches.length > 0) {
            e.preventDefault();
            onPick(matches[0]);
            setOpen(false);
          }
        }}
        className="w-full border rounded px-3 py-2 text-sm" />
      {open && matches.length > 0 && (
        <div className="absolute z-50 left-0 right-0 top-full mt-1 bg-white border rounded-lg shadow-lg max-h-52 overflow-y-auto">
          {matches.map((m, i) => (
            <button type="button" key={m.id ?? i} onMouseDown={(e) => e.preventDefault()}
              onClick={() => { onPick(m); setOpen(false); }}
              className="w-full text-left px-3 py-2 hover:bg-gray-50 text-sm truncate">
              {render(m)}
            </button>
          ))}
        </div>
      )}
      {/* Typed text alone never links anything - only picking a row (or
          Enter) does. Without this, agents assumed typing the address was
          enough and had no idea why nothing seemed to happen. */}
      {q.trim().length >= 2 && matches.length === 0 && searched && (
        <p className="text-xs text-gray-400 mt-1">No matches for "{q}" — that's fine, this field is optional; leave it blank to continue.</p>
      )}
      {q.trim().length >= 2 && matches.length > 0 && (
        <p className="text-xs text-gray-400 mt-1">Select a result below (or press Enter for the top match) to link it.</p>
      )}
    </div>
  );
}

// ---- Authority modal (link a deal for auto-fill + recipients -> instantiate -> editor) ----
function AuthorityModal({ authorityKey, onClose, onCreated }) {
  const [meta, setMeta] = useState(null);
  const [contact, setContact] = useState(null);
  const [property, setProperty] = useState(null);
  // Typed but unmatched property text (a brand-new listing not yet in the CRM) -
  // carried through as property_address so it's not lost between this modal and
  // the next screen (CG, 2026-08-10: "Property address not auto populating from
  // a address not in the crm").
  const [propertyAddressText, setPropertyAddressText] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [recipients, setRecipients] = useState([{ name: '', email: '', role: 'signer', order: 2 }]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let live = true;
    esignApi.listAuthorities()
      .then(({ data }) => {
        if (!live) return;
        const m = (data || []).find((a) => a.key === authorityKey) || { name: 'Authority' };
        setMeta(m);
        setSubject((s) => s || m.name || '');
      })
      .catch(() => setMeta({ name: 'Authority' }));
    return () => { live = false; };
  }, [authorityKey]);

  const searchContacts = useCallback(async (text) => {
    if (!text || text.trim().length < 2) return [];
    const { data } = await getContacts({ search: text, page_size: 8 });
    return Array.isArray(data) ? data : (data.results || []);
  }, []);
  const searchProps = useCallback(async (text) => {
    if (!text || text.trim().length < 2) return [];
    const { data } = await searchProperties({ search: text, page_size: 8 });
    return Array.isArray(data) ? data : (data.results || data.properties || []);
  }, []);

  // Picking the vendor contact prefills the first recipient row (if still blank).
  // personDisplayName, not c.name: plenty of contacts were imported with their
  // email address in the name column, and this name is PRINTED on the authority
  // as the vendor's name (Simon Abbott, Monday 12985884246). Better to leave it
  // blank for the agent to type than to print an email address.
  const pickContact = (c) => {
    setContact(c);
    setRecipients((rs) => rs.map((r, i) =>
      (i === 0 && !r.name && !r.email)
        ? { ...r, name: personDisplayName(c), email: c.email || '' } : r));
  };

  const addRecipient = () =>
    setRecipients((r) => [...r, { name: '', email: '', role: 'signer', order: r.length + 2 }]);
  const removeRecipient = (i) => setRecipients((r) => r.filter((_, idx) => idx !== i));

  const submit = async () => {
    const valid = dedupeRecipients(recipients);
    if (!valid.length) return toast.error('Add at least one recipient (the vendor’s name and email).');
    setSaving(true);
    try {
      const { data } = await esignApi.createFromAuthority({
        authority: authorityKey,
        contact_id: contact?.id,
        property_id: property?.id,
        // Only sent when no property was linked - carries a brand-new listing's
        // typed address through even though it doesn't match a CRM property.
        property_address: !property && propertyAddressText.trim() ? propertyAddressText.trim() : undefined,
        subject: subject.trim() || undefined,
        message: message.trim() || undefined,
        recipients: valid,
      });
      toast.success('Authority created and pre-filled.');
      onCreated(data.id);
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Could not create the authority.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl w-full max-w-lg p-6 shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-lg font-semibold">{meta?.name || 'Authority'}</h3>
          <button onClick={onClose}><X className="w-5 h-5 text-gray-500" /></button>
        </div>
        <p className="text-sm text-gray-500 mb-4">
          Link the vendor and property to auto-fill the agent, vendor and property details.
          You’ll review and complete the rest in the form on the next screen.
        </p>

        {/* Deal link (drives auto-fill of the document fields) */}
        <DealPicker
          label="Vendor (contact)" placeholder="Search contacts by name or email"
          search={searchContacts} value={contact}
          render={(c) => `${c.name || '—'}${c.email ? ` · ${c.email}` : ''}`}
          onPick={pickContact} onClear={() => setContact(null)} />

        {/* Optional, not required - these authorities are also used to sign up
            brand-new listings that aren't in the CRM yet, so there's often no
            property to link at all (CG, 2026-08-06). Just leave it blank and
            fill the address in on the next screen. Whatever's typed here still
            carries through as property_address even without a match, so a
            non-CRM address isn't lost between this screen and the next. */}
        <DealPicker
          label="Property" placeholder="Search properties by address (leave blank for a new listing)"
          search={searchProps} value={property}
          render={(p) => p.address || p.title || `Property #${p.id}`}
          onPick={(p) => { setProperty(p); setPropertyAddressText(''); }}
          onClear={() => setProperty(null)}
          onQueryChange={setPropertyAddressText} />

        <label className="block text-sm font-medium mb-1">Email subject</label>
        <input value={subject} onChange={(e) => setSubject(e.target.value)}
          className="w-full border rounded px-3 py-2 mb-3 text-sm" />

        <label className="block text-sm font-medium mb-1">Message to recipients</label>
        <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={2}
          className="w-full border rounded px-3 py-2 mb-4 text-sm" />

        {/* Signers */}
        <label className="block text-sm font-medium mb-1">Who signs</label>
        <p className="text-xs text-gray-400 mb-2">Add the vendor (and anyone else) who needs to sign. You don’t sign — you complete the details on the next screen.</p>
        {recipients.map((r, i) => (
          <RecipientRow key={i} r={r} signingOrder={false}
            canRemove={recipients.length > 1}
            onChange={(patch) => setRecipients((rs) => rs.map((x, idx) => (idx === i ? { ...x, ...patch } : x)))}
            onRemove={() => removeRecipient(i)} />
        ))}
        <button onClick={addRecipient} className="text-sm text-teal-600 mb-5">+ Add recipient</button>

        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm rounded border">Cancel</button>
          <button onClick={submit} disabled={saving}
            className="px-4 py-2 text-sm rounded bg-black text-white disabled:opacity-50">
            {saving ? 'Creating…' : 'Create & review'}
          </button>
        </div>
      </div>
    </div>
  );
}
